import assetDepartmentService from "../../../../services/assetService/assetDepartmentService";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { CACHE_KEY_ASSETDEPARTMENTS } from "../../../../utils/constants";
import { AssetDepartmentInterface } from "../../../../models/AssetModel/AssetDepartment";

interface UpdateAssetDepartmentContext {
  previousAssetDepartment: AssetDepartmentInterface[];
}

const useUpdateAssetDepartment = (page: number, pageSize: number) => {
  const queryClient = useQueryClient();

  return useMutation<
    AssetDepartmentInterface,
    Error,
    AssetDepartmentInterface,
    UpdateAssetDepartmentContext
  >({
    mutationFn: async (asset: AssetDepartmentInterface) => {
      // Assuming the ID is stored in the asset object or can be derived
      const { id, ...assetData } = asset;
      return assetDepartmentService.Update(id || "", assetData);
    },

    onMutate: async (updatedAssetDepartment: AssetDepartmentInterface) => {
      await queryClient.cancelQueries({
        queryKey: [
          CACHE_KEY_ASSETDEPARTMENTS,
          page > 0 ? page : null,
          pageSize > 0 ? pageSize : null,
        ],
      });

      const previousAssetDepartment =
        queryClient.getQueryData<AssetDepartmentInterface[]>([
          CACHE_KEY_ASSETDEPARTMENTS,
          page > 0 ? page : null,
          pageSize > 0 ? pageSize : null,
        ]) || [];

      queryClient.setQueryData<AssetDepartmentInterface[]>(
        [
          CACHE_KEY_ASSETDEPARTMENTS,
          page > 0 ? page : null,
          pageSize > 0 ? pageSize : null,
        ],
        (assetDepartments = []) =>
          assetDepartments.map((assetDepartment) =>
            assetDepartment.id === updatedAssetDepartment.id
              ? updatedAssetDepartment
              : assetDepartment
          )
      );

      return { previousAssetDepartment };
    },

    onSuccess: (savedAssetDepartment) => {
      queryClient.setQueryData<AssetDepartmentInterface[]>(
        [CACHE_KEY_ASSETDEPARTMENTS, page, pageSize],
        (assetDepartments) =>
          assetDepartments?.map((asset) =>
            asset.id === savedAssetDepartment.id ? savedAssetDepartment : asset
          )
      );
    },

    onError: (error, updatedAssetDepartment, context) => {
      if (!context) return;

      queryClient.setQueryData<AssetDepartmentInterface[]>(
        [CACHE_KEY_ASSETDEPARTMENTS, page, pageSize],
        context.previousAssetDepartment
      );
    },
  });
};

export default useUpdateAssetDepartment;
